(() => {
  'use strict';
  if (customElements.get('fxl-collection-catalog')) return;
  class Catalog extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.strings = JSON.parse(this.querySelector('[data-catalog-strings]').textContent);
      this.money = new Intl.NumberFormat(this.dataset.locale || 'nl', {style:'currency', currency:this.dataset.currency || 'EUR'});
      this.products = new Map();
      this.cards = new Map();
      this.groups = new Map();
      this.sections = new Map();
      this.expanded = new Set();
      this.pages = new Set();
      this.limit = Number(this.dataset.initialLimit) || 50;
      this.mobile = matchMedia('(max-width: 767px)');
      this.locationKey = location.pathname + location.search;
      this.nav = [...this.querySelectorAll('[data-catalog-nav]')].map(link => ({id:link.dataset.catalogNav, handle:link.dataset.catalogHandle, title:link.dataset.catalogTitle, link}));
      this.baseNav=this.nav.filter(entry=>entry.id!=='other');
      this.groupOrder=JSON.parse(this.dataset.groupOrder || '[]');
      this.popular=JSON.parse(this.dataset.popular || '[]').map(String);
      this.on(this,'click',event => this.click(event));
      this.on(this,'change',event => this.change(event));
      this.on(this,'submit',event => {
        if (event.target.matches('[data-fxlc-facets]')) { event.preventDefault(); if (this.validFacets()) this.update(this.facetURL()); }
      });
      this.on(window,'popstate',() => {
        if (this.locationKey !== location.pathname + location.search) this.update(new URL(location.href),false);
      });
      this.on(this.mobile,'change',() => this.filterMode());
      this.querySelectorAll('[data-fxlc-filter-open],[data-fxlc-filter-close]').forEach(button => { button.hidden=false; });
      const dialog=this.querySelector('[data-fxlc-filters]');
      if (dialog) this.on(dialog,'close',() => this.unlock());
      this.filterMode();
      const active=this.querySelector('[data-fxlc-active]')?.dataset.fxlcActive;
      const badge=this.querySelector('[data-fxlc-filter-count]');
      if (badge) badge.textContent=Number(active) ? `(${active})` : '';
      const source=this.querySelector('[data-catalog-page]');
      try {
        const data=JSON.parse(source.querySelector('[data-catalog-page-data]').textContent);
        if (Number(data.page)>1) {
          const url=new URL(location.href); url.searchParams.delete('page');
          history.replaceState(history.state,'',url);
          this.update(url,false,false); return;
        }
        this.ingest(source);
        this.dataset.enhanced='true';
        source.hidden=true;
        this.complete=!this.next;
        this.render();
        this.loadAll();
      } catch (error) { this.fail(error,'refresh'); }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller=null;
      this.request?.abort(); this.previewRequest?.abort();
      clearTimeout(this.previewTimer);
      this.observer?.disconnect(); this.unlock();
    }
    on(target,event,handler) { target.addEventListener(event,handler,{signal:this.controller.signal}); }
    t(key,count) { return (this.strings[key] || key).replaceAll('[count]',String(count ?? '')); }
    status(message) { this.querySelector('[data-fxlc-status]').textContent=message; }
    productCount(count) { return count===1 ? this.t('product_one') : this.t('products',count); }
    summary(groupCount,productCount) {
      if (groupCount===productCount) return this.productCount(productCount);
      return `${groupCount===1 ? this.t('group_one') : this.t('groups',groupCount)} · ${productCount===1 ? this.t('variant_one') : this.t('variants',productCount)}`;
    }
    ingest(container) {
      const data=JSON.parse(container.querySelector('[data-catalog-page-data]').textContent);
      if (String(data.collection)!==this.dataset.collectionId || !Array.isArray(data.products) || this.pages.has(data.page)) throw new Error('Invalid catalog page');
      const rows=new Map(data.products.map(row=>[String(row.id),{...row,id:String(row.id),group:String(row.group),collections:row.collections.map(String)}]));
      container.querySelectorAll('[data-catalog-source] > .fxl-product-card').forEach(card => {
        const row=rows.get(card.dataset.productId);
        if (!row) throw new Error('Card without product');
        if (!this.cards.has(row.group)) this.cards.set(row.group,card.cloneNode(true));
      });
      for (const [id,row] of rows) {
        if (!this.cards.has(row.group)) throw new Error('Missing group card');
        this.products.set(id,row);
      }
      this.pages.add(data.page);
      this.expected=Number(data.total);
      this.next=data.next || null;
    }
    endpoint(next) {
      const url=new URL(location.href);
      const page=new URL(next,location.href).searchParams.get('page');
      if (!page) throw new Error('Missing page number');
      url.hash=''; url.searchParams.set('page',page);
      url.searchParams.set('section_id','fxl-collection-catalog-page');
      return url;
    }
    async loadAll() {
      if (this.loading) return;
      this.loading=true;
      this.querySelector('[data-fxlc-retry]').hidden=true;
      if (this.next) { this.status(this.t('loading')); this.querySelector('[data-fxlc-results]').setAttribute('aria-busy','true'); }
      try {
        while (this.next) {
          const firstPage=Number(new URL(this.next,location.href).searchParams.get('page'));
          const lastPage=Math.ceil(this.expected/250);
          const numbers=Array.from({length:Math.min(3,lastPage-firstPage+1)},(_,index)=>firstPage+index);
          if (!numbers.length) throw new Error('Invalid pagination');
          const pages=await Promise.all(numbers.map(async number=>{
            const response=await fetch(this.endpoint(`?page=${number}`),{signal:this.controller.signal,credentials:'same-origin'});
            if (!response.ok) throw new Error('Catalog request failed');
            const document=new DOMParser().parseFromString(await response.text(),'text/html');
            const page=document.querySelector('[data-catalog-page]');
            if (!page) throw new Error('Catalog response missing');
            return page;
          }));
          if (!this.isConnected) return;
          for (const page of pages) this.ingest(page);
          this.complete=!this.next;
          this.render();
        }
        if (this.products.size!==this.expected) { this.fail(new Error('Incomplete catalog'),'refresh'); return; }
        this.complete=true;
        this.render();
        this.status('');
        this.querySelector('[data-fxlc-results]').removeAttribute('aria-busy');
        this.dataset.ready='true';
        if (location.hash && !this.initialJumpDone) { this.initialJumpDone=true; requestAnimationFrame(()=>this.jump(location.hash,false)); }
      } catch(error) { if (error.name!=='AbortError') this.fail(error,'more'); }
      finally { this.loading=false; }
    }
    fail(error,mode) {
      this.retryMode=mode;
      this.complete=false;
      if (this.products.size) this.render();
      this.status(this.t('error'));
      this.querySelector('[data-fxlc-retry]').hidden=false;
      this.querySelector('[data-fxlc-results]').removeAttribute('aria-busy');
      this.removeAttribute('aria-busy');
    }
    render() {
      this.groups=new Map();
      for (const row of this.products.values()) {
        if (!this.groups.has(row.group)) this.groups.set(row.group,[]);
        this.groups.get(row.group).push(row);
      }
      const rows=[...this.products.values()];
      const seriesIds=new Set(this.baseNav.map(entry=>entry.id));
      const hasSeries=this.dataset.hasSeries==='true';
      const mode=this.dataset.groupMode;
      const categories=new Map();
      if (!hasSeries && mode!=='none') for (const row of rows) {
        if (row[mode+'_key'] && !categories.has(row[mode+'_key'])) categories.set(row[mode+'_key'],row[mode]);
      }
      const useCategories=categories.size>1;
      let nav=hasSeries ? this.baseNav.slice() : useCategories ? [...categories].map(([key,title])=>({id:`category:${key}`,handle:`ptype-${key}`,title})) : [{id:'all',handle:this.dataset.collection,title:this.dataset.mainTitle}];
      if (useCategories) nav.sort((a,b)=>{
        const rank=entry=>{const i=this.groupOrder.indexOf(entry.id.slice(9));return i<0 ? Number.MAX_SAFE_INTEGER : i;};
        return rank(a)-rank(b);
      });
      if (hasSeries || useCategories) nav.push({id:'other',handle:'overige',title:this.t('other')});
      if (this.dataset.collection!=='outlet' && rows.some(row=>row.outlet)) nav.push({id:'outlet',handle:'outlet',title:'Outlet'});
      if (this.popular.length) nav.unshift({id:'popular',handle:'populair',title:this.dataset.popularTitle || 'Populaire producten'});
      const entries=new Map(nav.map(item=>[item.id,new Map()]));
      const add=(id,row)=>{
        const groups=entries.get(id);
        if (!groups) return;
        if (!groups.has(row.group)) groups.set(row.group,[]);
        groups.get(row.group).push(row);
      };
      for (const row of rows) {
        const destinations=row.outlet && entries.has('outlet') ? ['outlet'] : hasSeries ? row.collections.filter(id=>seriesIds.has(id)) : useCategories ? [row[mode+'_key'] ? `category:${row[mode+'_key']}` : 'other'] : ['all'];
        if (!destinations.length) destinations.push('other');
        destinations.forEach(id=>add(id,row));
      }
      for (const id of this.popular) { const row=this.products.get(id); if (row?.available) add('popular',row); }
      const navElement=this.querySelector('.fxlc-catalog-nav');
      const links=new Map([...navElement.querySelectorAll('[data-catalog-nav]')].map(link=>[link.dataset.catalogNav,link]));
      for (const entry of nav) {
        let link=links.get(entry.id);
        if (!link) {
          link=document.createElement('a');link.href=`#series-${entry.handle}`;link.dataset.catalogNav=entry.id;
          const text=document.createElement('span'),title=document.createElement('strong'),count=document.createElement('small');
          title.textContent=entry.title;count.dataset.catalogCount='';text.append(title,count);link.append(text);
        }
        if (!link.querySelector('img')) {
          const first=[...entries.get(entry.id).values()][0]?.[0];
          const image=first && this.cards.get(first.group)?.querySelector('.fxl-product-card__image img');
          if (image) {
            const thumb=image.cloneNode();thumb.width=44;thumb.height=44;thumb.sizes='44px';thumb.alt='';
            link.querySelector('.fxlc-catalog-icon')?.remove();
            link.prepend(thumb);
          }
        }
        entry.link=link;navElement.append(link);
      }
      const activeIds=new Set(nav.map(entry=>entry.id));
      links.forEach((link,id)=>{if (!activeIds.has(id)) link.remove();});
      for (const [id,section] of this.sections) if (!activeIds.has(id)) section.hidden=true;
      this.nav=nav;
      const showNav=nav.filter(entry=>entries.get(entry.id).size>0).length>1;
      navElement.hidden=!showNav;
      const hasFilters=!!this.querySelector('.fxlc-catalog-sidebar [data-fxlc-facets]');
      const hasRelated=!!this.querySelector('.fxlc-catalog-sidebar .series-related__mainlink');
      const showSidebar=showNav||hasFilters||hasRelated;
      this.querySelector('.fxlc-catalog-sidebar').hidden=!showSidebar;
      this.querySelector('.fxlc-catalog-layout').classList.toggle('fxlc-catalog-layout--wide',!showSidebar);
      const jump=this.querySelector('[data-fxlc-jump]');
      const previousJump=jump?.value;
      if (jump) while (jump.options.length>1) jump.remove(1);
      for (const entry of this.nav) {
        const groups=entries.get(entry.id);
        const count=[...groups.values()].reduce((sum,rows)=>sum+rows.length,0);
        if (entry.link) {
          entry.link.hidden=this.complete && count===0;
          entry.link.querySelector('[data-catalog-count]').textContent=this.complete ? this.summary(groups.size,count) : this.t('partial');
        }
        if (!count) { this.sections.get(entry.id)?.setAttribute('hidden',''); continue; }
        if (jump) jump.add(new Option(entry.title,`#series-${entry.handle}`));
        let block=this.sections.get(entry.id);
        if (!block) {
          block=document.createElement('section');
          block.className='product-series-block fxlc-catalog-series'; block.id=`series-${entry.handle}`; block.tabIndex=-1;
          const heading=document.createElement('div'); heading.className='fxlc-catalog-series__heading';
          const title=document.createElement('h2'); title.textContent=entry.title; heading.append(title);
          const count=document.createElement('p'); count.dataset.catalogSeriesCount=''; heading.append(count);
          const grid=document.createElement('ul'); grid.className='l4cl fxlc-catalog-grid'; grid.id=`catalog-grid-${this.dataset.sectionId}-${entry.id}`;
          const expand=document.createElement('button'); expand.type='button'; expand.className='fxlc-catalog-expand'; expand.dataset.catalogExpand=entry.id; expand.setAttribute('aria-controls',grid.id);
          const save=document.createElement('button');save.type='button';save.className='fxlc-catalog-save';save.dataset.fxlfSeries='';save.textContent='Bewaar serie op feestlijst';save.hidden=true;
          block.append(heading,grid,expand,save); this.sections.set(entry.id,block);
        }
        block.hidden=false;
        block.querySelector('[data-catalog-series-count]').textContent=this.complete ? this.summary(groups.size,count) : this.t('partial');
        const expanded=this.expanded.has(entry.id);
        const grid=block.querySelector('.fxlc-catalog-grid');
        const ordered=[...groups.entries()];
        const sort=this.dataset.sort;
        if (sort==='price-ascending') ordered.sort((a,b)=>Math.min(...a[1].map(p=>p.price))-Math.min(...b[1].map(p=>p.price)));
        if (sort==='price-descending') ordered.sort((a,b)=>Math.min(...b[1].map(p=>p.price))-Math.min(...a[1].map(p=>p.price)));
        const visible=expanded ? ordered : ordered.slice(0,this.limit);
        const keep=new Set(visible.map(([key])=>key));
        [...grid.children].forEach(card=>{ if (!keep.has(card.dataset.catalogGroup)) card.remove(); });
        for (const [key,rows] of visible) {
          let card=[...grid.children].find(card=>card.dataset.catalogGroup===key);
          if (!card) { card=this.cards.get(key).cloneNode(true); card.dataset.catalogGroup=key; }
          if (card.hasAttribute('data-family-card')) {
            this.selectFamilyOption(card.querySelector('[data-family-option][aria-pressed="true"]') || card.querySelector('[data-family-option]'));
          }
          grid.append(card);
        }
        const save=block.querySelector('[data-fxlf-series]');
        save.hidden=!this.complete || !document.querySelector('[data-fxlf-config]') || ordered.some(([key])=>this.cards.get(key).hasAttribute('data-family-card'));
        const expand=block.querySelector('[data-catalog-expand]');
        expand.hidden=groups.size<=this.limit;
        expand.textContent=expanded ? this.t('collapse') : this.t('expand',groups.size);
        expand.setAttribute('aria-expanded',String(expanded));
        this.querySelector('[data-catalog-sections]').append(block);
      }
      if (jump) {
        if ([...jump.options].some(option=>option.value===previousJump)) jump.value=previousJump;
        jump.closest('[data-fxlc-jump-wrap]').hidden=jump.options.length<2;
      }
      this.querySelector('[data-catalog-empty]').hidden=!(this.complete&&this.products.size===0);
      if (this.complete) this.querySelector('[data-fxlc-total]').textContent=this.summary(this.groups.size,this.products.size);
      this.observe();
    }
    observe() {
      this.observer?.disconnect();
      this.observer=new IntersectionObserver(entries=>{
        const active=entries.filter(entry=>entry.isIntersecting).sort((a,b)=>a.boundingClientRect.top-b.boundingClientRect.top)[0];
        if (!active) return;
        for (const entry of this.nav) if (entry.link) {
          const current=active.target===this.sections.get(entry.id);
          entry.link.classList.toggle('is-active',current);
          if (current) entry.link.setAttribute('aria-current','location'); else entry.link.removeAttribute('aria-current');
        }
      },{rootMargin:'-170px 0px -55% 0px'});
      for (const block of this.sections.values()) if (!block.hidden) this.observer.observe(block);
    }
    jump(hash,push=true) {
      let id; try { id=decodeURIComponent(hash.slice(1)); } catch { return; }
      const block=[...this.sections.values()].find(block=>block.id===id);
      if (!block || block.hidden) return;
      block.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
      block.focus({preventScroll:true});
      if (push) history.replaceState(history.state,'',hash);
    }
    selectFamilyOption(option) {
      if (!option) return;
      const card=option.closest('[data-family-card]');
      const data=option.dataset;
      if (!card || !data.optionUrl) return;
      card.querySelectorAll('[data-family-option]').forEach(button=>button.setAttribute('aria-pressed',String(button===option)));
      card.dataset.selectedProductId=data.optionId;
      const stock=card.querySelector('[data-family-stock-current]');
      const stockTemplate=[...card.querySelectorAll('template[data-family-stock]')].find(template=>template.dataset.familyStock===data.optionId);
      if (stock && stockTemplate) stock.replaceChildren(stockTemplate.content.cloneNode(true));
      card.querySelector('[data-family-price]').textContent=this.money.format(Number(data.optionPrice)/100);
      card.querySelector('[data-family-from]').hidden=data.optionPriceVaries!=='true';
      const imageLink=card.querySelector('.fxl-product-card__image');
      const titleLink=card.querySelector('[data-family-title]');
      const chooseLink=card.querySelector('.fxl-product-card__choose');
      [imageLink,titleLink].forEach(link=>{ link.href=data.optionUrl; });
      const familyURL=data.optionUrl+(card.dataset.familyAnchor || '');
      chooseLink.href=familyURL;
      const moreLink=card.querySelector('[data-family-more]');
      if (moreLink) moreLink.href=familyURL;
      titleLink.textContent=data.optionTitle;
      imageLink.setAttribute('aria-label',data.optionTitle);
      titleLink.setAttribute('aria-label',data.optionTitle);
      chooseLink.setAttribute('aria-label',`${chooseLink.textContent.trim()}: ${data.optionTitle}`);
      let image=imageLink.querySelector('img');
      if (data.optionImage) {
        if (!image) {
          image=document.createElement('img'); image.loading='lazy';
          image.sizes='(max-width: 760px) 45vw, (max-width: 1100px) 30vw, 250px';
          image.width=640; image.height=640; imageLink.prepend(image);
        }
        image.alt=data.optionTitle;
        image.srcset=data.optionSrcset || '';
        image.src=data.optionImage;
      }
      if (image) image.hidden=!data.optionImage;
      const placeholder=imageLink.querySelector('[data-family-placeholder]');
      if (placeholder) placeholder.hidden=Boolean(data.optionImage);
    }
    click(event) {
      const target=event.target.closest('button,a');
      if (!target || !this.contains(target)) return;
      if (target.matches('a') && (event.metaKey||event.ctrlKey||event.shiftKey||event.altKey||event.button!==0)) return;
      if (target.hasAttribute('data-family-option')) {
        event.preventDefault(); this.selectFamilyOption(target);
      } else if (target.hasAttribute('data-fxlf-series')) {
        const entry=this.nav.find(item=>this.sections.get(item.id)===target.closest('.fxlc-catalog-series'));
        if (entry) {this.expanded.add(entry.id);this.render();}
      } else if (target.hasAttribute('data-catalog-expand')) {
        const id=target.dataset.catalogExpand;
        if (this.expanded.has(id)) { this.expanded.delete(id); this.render(); this.sections.get(id)?.scrollIntoView({block:'start'}); }
        else { this.expanded.add(id); this.render(); }
      } else if (target.hasAttribute('data-catalog-nav')) { event.preventDefault(); this.jump(target.hash); }
      else if (target.hasAttribute('data-fxlc-update')) { event.preventDefault(); this.update(new URL(target.href)); }
      else if (target.hasAttribute('data-fxlc-filter-open')) {
        const dialog=this.querySelector('[data-fxlc-filters]'); dialog.showModal(); this.lock();
      } else if (target.hasAttribute('data-fxlc-filter-close')) this.querySelector('[data-fxlc-filters]').close();
      else if (target.hasAttribute('data-fxlc-retry')) {
        if (this.retryMode==='more') this.loadAll(); else this.update(this.lastURL || new URL(location.href),false);
      }
    }
    change(event) {
      if (event.target.matches('[data-fxlc-jump]')) { if (event.target.value) this.jump(event.target.value); return; }
      if (!event.target.matches('[data-fxlc-sort]') && !event.target.closest('[data-fxlc-facets]')) return;
      if (!this.validFacets()) return;
      if (this.mobile.matches && event.target.closest('[data-fxlc-facets]')) {
        clearTimeout(this.previewTimer); this.previewRequest?.abort();
        this.previewTimer=setTimeout(()=>this.previewFacets(),150);
      } else this.update(this.facetURL());
    }
    validFacets() {
      const form=this.querySelector('[data-fxlc-facets]');
      const inputs=form?.querySelectorAll('.fxlc-price-range input');
      if (inputs?.length===2) inputs[1].min=inputs[0].value || '0';
      return !form || form.reportValidity();
    }
    facetURL() {
      const url=new URL(location.href); url.hash='';
      [...url.searchParams.keys()].forEach(key=>{if (key.startsWith('filter.')||['sort_by','page','section_id'].includes(key)) url.searchParams.delete(key);});
      const form=this.querySelector('[data-fxlc-facets]');
      if (form) for (const [key,value] of new FormData(form)) if (String(value).trim()) url.searchParams.append(key,value);
      const sort=this.querySelector('[data-fxlc-sort]');
      if (sort) url.searchParams.set('sort_by',sort.value);
      return url;
    }
    async fetchSection(url,signal) {
      const endpoint=new URL(url); endpoint.searchParams.set('section_id',this.dataset.sectionId);
      if (!endpoint.searchParams.has('preview_theme_id')) {
        const preview=new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) endpoint.searchParams.set('preview_theme_id',preview);
      }
      const response=await fetch(endpoint,{signal,credentials:'same-origin'});
      if (!response.ok) throw new Error('Collection request failed');
      const document=new DOMParser().parseFromString(await response.text(),'text/html');
      const fresh=document.querySelector('fxl-collection-catalog');
      if (!fresh || fresh.dataset.sectionId!==this.dataset.sectionId) throw new Error('Invalid collection response');
      return fresh;
    }
    async update(url,push=true,scroll=true) {
      this.lastURL=url; this.request?.abort(); this.previewRequest?.abort(); clearTimeout(this.previewTimer);
      const request=this.request=new AbortController();
      this.setAttribute('aria-busy','true'); this.status(this.t('loading')); this.querySelector('[data-fxlc-retry]').hidden=true;
      try {
        const fresh=await this.fetchSection(url,request.signal);
        if (request!==this.request || request.signal.aborted) return;
        this.querySelectorAll('dialog[open]').forEach(dialog=>dialog.close());
        const preview=new URL(location.href).searchParams.get('preview_theme_id');
        if (preview&&!url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id',preview);
        if (push) history.pushState({},'',url);
        this.replaceWith(fresh);
        if (scroll) {
          fresh.querySelector('[data-fxlc-toolbar]').scrollIntoView({block:'start'});
          fresh.querySelector('[data-fxlc-results]').focus({preventScroll:true});
        }
      } catch(error) { if(error.name!=='AbortError') this.fail(error,'refresh'); }
    }
    async previewFacets() {
      const request=this.previewRequest=new AbortController();
      const button=this.querySelector('[data-fxlc-apply]'); button.textContent=this.t('loading');
      try {
        const fresh=await this.fetchSection(this.facetURL(),request.signal);
        if (request!==this.previewRequest||request.signal.aborted) return;
        button.textContent=this.t('apply',fresh.dataset.total);
      } catch(error) { if(error.name!=='AbortError') button.textContent=this.t('apply','…'); }
    }
    filterMode() {
      const dialog=this.querySelector('[data-fxlc-filters]'); if (!dialog) return;
      if (dialog.open) dialog.close();
      if (!this.mobile.matches) dialog.setAttribute('open','');
    }
    lock() { if (this.previousOverflow===undefined) {this.previousOverflow=document.documentElement.style.overflow;document.documentElement.style.overflow='hidden';} }
    unlock() { if (this.previousOverflow!==undefined) {document.documentElement.style.overflow=this.previousOverflow;delete this.previousOverflow;} }
  }
  customElements.define('fxl-collection-catalog',Catalog);
})();




